'use client'

import { useRef, ReactNode } from 'react'
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { ChevronLeft, ChevronRight } from 'lucide-react'
import SectionHeader from "@modules/common/components/section-header"

interface CarouselSectionProps<T> {
  /** Section title displayed in the header */
  title: string
  /** Unique ID for accessibility (used in aria-labelledby) */
  sectionId: string
  /** Array of items to display in the carousel */
  items: T[]
  /** Render function for each item - receives the item and its index */
  renderItem: (item: T, index: number) => ReactNode
  /** Optional content to display below the carousel (e.g., featured shirts grid) */
  children?: ReactNode
  /** Optional content to display between header and carousel (e.g., filter tabs) */
  headerContent?: ReactNode
  /** Show "View All" link in header */
  showViewAll?: boolean
  /** Hide View All button on mobile only (still visible on desktop) */
  hideViewAllOnMobile?: boolean
  /** URL for "View All" link */
  viewAllHref?: string
  /** Custom text for View All button (defaults to "View All") */
  viewAllText?: string
  /** Aria label for the carousel list */
  ariaLabel?: string
  /** Scroll amount in pixels when clicking navigation arrows */
  scrollAmount?: number
  /** Gap between carousel items (Tailwind class, e.g., 'gap-4' or 'gap-6') */
  gap?: string
  /** Background color variant */
  background?: 'white' | 'gray' | 'dark'
  /** Vertical padding variant */
  padding?: 'none' | 'xs' | 'sm' | 'md' | 'lg'
  /** Custom class for title styling */
  titleClassName?: string
  /** Align navigation arrows with headerContent instead of title */
  alignNavWithContent?: boolean
  /** Show View All button next to title (used with alignNavWithContent) */
  showTitleViewAll?: boolean
  /** URL for title View All button */
  titleViewAllHref?: string
  /** Extract a unique key from each item for React list rendering.
   * Defaults to using the array index if not provided. */
  keyExtractor?: (item: T, index: number) => string | number
}

const paddingStyles = {
  none: '',
  xs: 'py-3 md:py-5',
  sm: 'py-5 md:py-10',
  md: 'py-5 md:py-10',
  lg: 'py-5 md:py-10',
}

const backgroundStyles = {
  white: 'bg-white',
  gray: 'bg-[#F7F7F7]',
  dark: 'bg-brand-hero-navy',
}

export default function CarouselSection<T>({
  title,
  sectionId,
  items,
  renderItem,
  children,
  headerContent,
  showViewAll = false,
  hideViewAllOnMobile = false,
  viewAllHref = '#',
  viewAllText = 'View All',
  ariaLabel,
  scrollAmount = 200,
  gap = 'gap-4',
  background = 'white',
  padding = 'lg',
  titleClassName = '',
  alignNavWithContent = false,
  showTitleViewAll = false,
  titleViewAllHref = '#',
  keyExtractor = (_, index) => index,
}: CarouselSectionProps<T>) {
  const carouselRef = useRef<HTMLDivElement>(null)
  const isDark = background === 'dark'

  const scrollLeft = () => {
    carouselRef.current?.scrollBy({ left: -scrollAmount, behavior: 'smooth' })
  }

  const scrollRight = () => {
    carouselRef.current?.scrollBy({ left: scrollAmount, behavior: 'smooth' })
  }

  return (
    <section
      className={`${paddingStyles[padding]} ${backgroundStyles[background]} overflow-hidden`}
      aria-labelledby={`${sectionId}-heading`}
    >
      <div className="max-w-screen-xl mx-auto px-4">
        {alignNavWithContent && headerContent ? (
          <>
            {/* Title row - reduced spacing */}
            <div className="flex items-center gap-3 mb-2 md:mb-3">
              <h2 className={`text-[22px] md:text-[36px] font-black ${isDark ? 'text-white' : 'text-brand-navy'} ${titleClassName}`}>
                {title}
              </h2>
              {showTitleViewAll && titleViewAllHref && (
                <LocalizedClientLink
                  href={titleViewAllHref}
                  className={`hidden md:inline-flex text-sm font-medium transition-colors items-center gap-1 rounded-[4px] px-2 py-0.5 outline-none focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:ring-offset-2 rounded ${
                    isDark
                      ? 'text-white border border-white hover:bg-white/10'
                      : 'text-black border border-black hover:bg-gray-50'
                  }`}
                >
                  View All
                  <ChevronRight className="w-4 h-4" aria-hidden="true" />
                </LocalizedClientLink>
              )}
            </div>
            {/* Header content + Navigation row */}
            <div className="flex items-center justify-between mb-3 md:mb-6">
              {headerContent}
              <div className="hidden md:flex gap-2">
                <button
                  onClick={scrollLeft}
                  className={`p-2.5 rounded-full transition-colors outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                    isDark
                      ? 'bg-white/20 hover:bg-white/30 focus-visible:ring-white'
                      : 'bg-gray-100 hover:bg-gray-200 focus-visible:ring-brand-navy'
                  }`}
                  aria-label="Previous items"
                >
                  <ChevronLeft className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-500'}`} aria-hidden="true" />
                </button>
                <button
                  onClick={scrollRight}
                  className={`p-2.5 rounded-full transition-colors outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                    isDark
                      ? 'bg-white/20 hover:bg-white/30 focus-visible:ring-white'
                      : 'bg-gray-100 hover:bg-gray-200 focus-visible:ring-brand-navy'
                  }`}
                  aria-label="Next items"
                >
                  <ChevronRight className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-500'}`} aria-hidden="true" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <SectionHeader
              title={title}
              showViewAll={showViewAll}
              hideViewAllOnMobile={hideViewAllOnMobile}
              viewAllHref={viewAllHref}
              viewAllText={viewAllText}
              showNavigation
              onPrevious={scrollLeft}
              onNext={scrollRight}
              titleClassName={titleClassName}
            />
            {headerContent}
          </>
        )}

        {/* Carousel scroll area — clipped at same boundary as header content */}
        <div className="overflow-hidden">
          <div
            ref={carouselRef}
            className="overflow-x-auto md:overflow-x-hidden scrollbar-hide snap-x snap-mandatory md:snap-none"
          >
            <div
              className={`flex ${gap} pb-2`}
              role="list"
              aria-label={ariaLabel || `${title} items`}
            >
              {items.map((item, index) => (
                <div key={keyExtractor(item, index)} role="listitem" className="flex-shrink-0 snap-start md:snap-align-none">
                  {renderItem(item, index)}
                </div>
              ))}
            </div>
          </div>
        </div>

        {children}
      </div>
    </section>
  )
}
